import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { ICellRendererParams } from 'ag-grid-community';
import { UserStore } from '../../store/UserStore';
import { GroupStore } from '../../store/GroupStore';
import { LocaleStore } from '../../store';
import { ModalStore } from '../../store/ModalStore';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import { ApplicationStore, PharmacyStore, SecretariatStore, AuthenticationStore, PharmacyStatusStore } from '../../store';
import styled from '@emotion/styled';
import MainSectionLayout from '../layout/MainSectionLayout';
import { saveAs } from 'file-saver';
import {
    DataGrid,
    Column,
    Button as DGButton,
    SearchPanel,
    DataGridTypes,
    Export,
    FilterRow,
    GroupPanel,
    Grouping,
    HeaderFilter,
    Pager,
    Paging,
    FilterPanel,
    ColumnChooser
} from 'devextreme-react/data-grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import withTranslate from '../common/WithTranslate';
import { PHARMACY_STATUS_AC, PHARMACY_STATUS_TC, PHARMACY_STATUS_PC } from '../../common/constants/pharmacyStatusConstants';

interface SecretariatGroupsTabProps {
    secretariatId: number;
    UserStore?: Instance<typeof UserStore>;
    GroupStore?: Instance<typeof GroupStore>;
    LocaleStore?: Instance<typeof LocaleStore>;
    ModalStore?: Instance<typeof ModalStore>;
    ApplicationStore?: Instance<typeof ApplicationStore>;
    PharmacyStore?: Instance<typeof PharmacyStore>;
    SecretariatStore?: Instance<typeof SecretariatStore>;
    AuthenticationStore?: Instance<typeof AuthenticationStore>;
    PharmacyStatusStore?: Instance<typeof PharmacyStatusStore>;
}

interface SecretariatGroupsTabState {
    isLoading: boolean;
    api?: any;
    columnApi?: any;
}

export function renderStatus(params: ICellRendererParams) {
    const { value } = params;
    return value ? '' : 'no';
}

@inject(
    'UserStore',
    'GroupStore',
    'LocaleStore',
    'ModalStore',
    'ApplicationStore',
    'PharmacyStore',
    'SecretariatStore',
    'AuthenticationStore',
    'PharmacyStatusStore',
)
@observer
class SecretariatGroupsTab extends React.Component<SecretariatGroupsTabProps, SecretariatGroupsTabState> {
    deleteUser = async (id: number) => {
        const confirm = await this.props.ModalStore!.confirm.show('title', 'Are you sure you want to delete user?');
        if (confirm) {
            await this.props.UserStore!.deleteUsers([id]);
        }
    };

    isReadonlyMode = () => {
        return !(this.props.AuthenticationStore!.isAdmin || this.props.AuthenticationStore!.isSuperAdmin);
    };

    constructor(props: { secretariatId: number }) {
        super(props);
        this.state = {
            isLoading: false,
        };
    }

    _isMounted = false;

    async componentDidMount() {
        this._isMounted = true;
        const { GroupStore, PharmacyStore } = this.props;
        this._isMounted && this.setState({ isLoading: true });
        if (!PharmacyStore!.isListLoaded()) PharmacyStore!.loadList();
        await GroupStore!.checkListLoaded();
        this._isMounted && this.setState({ isLoading: false });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    buildGridData = () => {
        return this.props
            .GroupStore!.dataList.filter(
                (g) => g.secretariatId === this.props.secretariatId,
            )
            .map((group) => {
                let secretariat = this.props.SecretariatStore!.getById(group.secretariatId!);
                let statuses = this.props.PharmacyStatusStore!.dataList;
                let activeStatus = statuses.find((s) => s.code == PHARMACY_STATUS_AC)!;
                let temporaryClosedStatus = statuses.find((s) => s.code == PHARMACY_STATUS_TC)!;
                let permanentlyClosedStatus = statuses.find((s) => s.code == PHARMACY_STATUS_PC)!;
                return {
                    id: group.groupId,
                    name: group.name,
                    description: group.description,
                    memberCount: this.props.PharmacyStore!.dataList.filter((p) => p.groupId == group.groupId).length,
                    activeMemberCount: this.props.PharmacyStore!.dataList.filter((p) => p.groupId == group.groupId && p.statusId === activeStatus.pharmacyStatusId).length,
                    tempInactiveCount: this.props.PharmacyStore!.dataList.filter((p) => p.groupId == group.groupId && p.statusId === temporaryClosedStatus.pharmacyStatusId).length,
                    closedCount: this.props.PharmacyStore!.dataList.filter((p) => p.groupId == group.groupId && p.statusId === permanentlyClosedStatus.pharmacyStatusId).length,
                    color: group.color,
                    targetDutyCount: group.targetDutyCount,
                    targetDayDutyCount: group.targetDayDutyCount,
                    targetNightDutyCount: group.targetNightDutyCount,
                    secretariat: secretariat ? secretariat!.code : undefined
                };
            });
    };

    onExporting = (e: DataGridTypes.ExportingEvent) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                let fileName = 'Groupes.xlsx';

                if (this.props.AuthenticationStore!.isSuperAdmin && (this.props.secretariatId === undefined || this.props.secretariatId === 0)) {
                    fileName = 'Groupes_tous_secrétariats.xlsx';
                }
                else {
                    const secretariat = this.props.SecretariatStore!.getById(this.props.secretariatId!);

                    if (secretariat)
                        fileName = 'Groupes_' + secretariat.code + '.xlsx';
                }

                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
            });
        });
    };

    onRowClicked = (ev) => {
        this.props.ApplicationStore!.navigate(`${Page.GroupPage}/${ev.data.id}?from=secretariatGroupTab&secretariatId=${this.props.secretariatId}`);
    };

    public render() {
        return (
            <MainSectionLayout isLoading={this.state.isLoading} title={TLCode.GROUPS_Title}>
                <ContainerCol>
                    <DataGrid
                        dataSource={this.buildGridData()}
                        showBorders={true}
                        onExporting={this.onExporting}
                        onRowClick={this.onRowClicked}
                        allowColumnReordering={true}
                        rowAlternationEnabled={true}
                        width="100%"
                        allowColumnResizing={true}
                        columnResizingMode={'widget'}
                    >
                        <GroupPanel visible={true} />
                        <Grouping autoExpandAll={false} />
                        <FilterRow visible={true} applyFilter={'auto'} />
                        <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
                        <HeaderFilter visible={true} />
                        <FilterPanel visible={true} />
                        <Export enabled={true} />
                        <Paging defaultPageSize={20} />
                        <Pager visible={true}
                            allowedPageSizes={[5, 10, 20, 50, 100]}
                            showPageSizeSelector={true}
                            showInfo={true}
                            showNavigationButtons={true} />
                        <ColumnChooser enabled={true} />
                        <Column
                            dataType="string"
                            dataField="name"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_Name)}
                            minWidth={100}
                            defaultSortOrder="asc"
                        />
                        <Column
                            dataType="string"
                            dataField="description"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_Description)}
                            minWidth={250}
                        />
                        <Column
                            dataType="number"
                            dataField="memberCount"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_MemberCount)}
                            minWidth={100}
                        />
                        <Column
                            dataType="number"
                            dataField="activeMemberCount"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_ActiveMemberCount)}
                            minWidth={100}
                        />
                        <Column
                            dataType="number"
                            dataField="tempInactiveCount"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_TemporarilyClosedMemberCount)}
                            minWidth={100}
                        />
                        <Column
                            dataType="number"
                            dataField="closedCount"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_ClosedMemberCount)}
                            minWidth={100}
                        />
                        <Column
                            dataType="string"
                            dataField="color"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_Color)}
                            width={100}
                        />
                        <Column
                            dataType="number"
                            dataField="targetDutyCount"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_TargetDutyCount)}
                            width={100}
                        />
                        <Column
                            dataType="number"
                            dataField="targetDayDutyCount"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_TargetDayDutyCount)}
                            width={100}
                        />
                        <Column
                            dataType="number"
                            dataField="targetNightDutyCount"
                            caption={this.props.LocaleStore!.getText(TLCode.GROUP_TargetNightDutyCount)}
                            width={100}
                        />
                        <Column caption="" type="buttons" width={60}>
                            <DGButton
                                hint={this.props.LocaleStore!.getText(TLCode.GENERAL_Edit)}
                                icon={'edit'}
                                onClick={(e) => {
                                    this.props.ApplicationStore!.navigate(`${Page.GroupPage}/${e.row!.data.id}?from=secretariatGroupTab&secretariatId=${this.props.secretariatId}`);
                                }}
                            />
                        </Column>
                        {/* <Toolbar>
              <Item location='before' name="searchPanel" />
            </Toolbar> */}
                        <Paging defaultPageSize={20} />
                        <Pager visible={true}
                            allowedPageSizes={[5, 10, 20, 50, 100]}
                            showPageSizeSelector={true}
                            showInfo={true}
                            showNavigationButtons={true} />
                    </DataGrid>
                </ContainerCol>
            </MainSectionLayout>
        );
    }
}

const ContainerCol = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
`;


export default withTranslate(SecretariatGroupsTab);